/**
 * ChartDialog.tsx — Unified chart popup for all pages (ADR-002).
 * v3.54 - 2026-02-24 - Story 9I: Fix bar click tooltip index reversal — label matching (ADR-002)
 * v3.53 - 2026-02-24 - Story 13: Import shared utils from chartUtils.ts (ADR-002)
 * v3.49 - 2026-02-24 - Story 9E: Chart-type-adaptive UI, unified popup for all pages
 * v3.47 - 2026-02-24 - Story 9C: Bar click tooltip with detail card (ADR-002)
 * v3.45 - 2026-02-24 - Story 9A/9B: Disable preview labels; popup sort toggle (ADR-002)
 * v3.43 - 2026-02-24 - Story 8E: Created (ADR-002)
 *
 * Single popup component used by Dashboard (home), Dashboards, and DashboardCharts.
 * Adapts UI (sort toggle, tooltip content, legend, height) based on chart type.
 */
import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, IconButton, Button, Chip, Stack,
  ToggleButtonGroup, ToggleButton, Popover, Card, CardContent, Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import SortIcon from '@mui/icons-material/Sort';
import PlotlyChart from './PlotlyChart';
import { PALETTES } from '../utils/colorPalettes';
import {
  formatTitle, hasMixedPolarity, sortReportData, calculateChartHeight,
  isBarType as isBarTypeFn, isPieType as isPieTypeFn, isLineType as isLineTypeFn,
  type SortMode,
} from '../utils/chartUtils'; // v3.53 - Story 13: shared utils
import type { ChartReportData } from '../services/api';

// v3.47 - Click tooltip detail (generalized for all chart types)
interface TooltipData {
  anchorEl: HTMLElement;
  label: string;
  value: number;
  pctOfTotal: number;
  sign: 'Positive' | 'Negative' | 'Zero';
  chartCategory: 'bar' | 'pie' | 'line' | 'waterfall' | 'other';
}

function formatCurrency(v: number): string {
  const abs = Math.abs(v);
  const formatted = abs.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 });
  return v < 0 ? `−${formatted}` : formatted;
}

// --- Props ---

interface ChartDialogProps {
  report: ChartReportData | null;
  onClose: () => void;
}

export default function ChartDialog({ report, onClose }: ChartDialogProps) {
  const plotRef = useRef<HTMLDivElement>(null);
  const [sortMode, setSortMode] = useState<SortMode>('abs');

  const title = report
    ? report.visualization?.labels?.title ?? formatTitle(report.title)
    : '';
  const subtitle = report?.visualization?.labels?.subtitle ?? report?.description ?? '';

  // v3.49 - Story 9E: Chart type classification (v3.53 - via shared chartUtils)
  const chartType = report?.visualization?.chartType ?? 'horizontal_bar';
  const isBarType = isBarTypeFn(chartType);
  const isPieType = isPieTypeFn(chartType);
  const isLineType = isLineTypeFn(chartType);
  const isWaterfall = chartType === 'waterfall';
  const chartCategory: TooltipData['chartCategory'] = isBarType ? 'bar' : isPieType ? 'pie' : isLineType ? 'line' : isWaterfall ? 'waterfall' : 'other';

  // Sort only applies to bar types
  const sortedReport = useMemo(() => {
    if (!report) return null;
    if (!isBarType) return report; // v3.49 - No sort for non-bar types
    return sortReportData(report, sortMode);
  }, [report, sortMode, isBarType]);

  // v3.49 - Dynamic height per chart type (v3.53 - via shared calculateChartHeight)
  const chartHeight = useMemo(() => {
    if (!report) return 450;
    const measureCol = report.yColumn;
    const usableCount = (isBarType || isWaterfall) && measureCol
      ? report.data.filter((r) => { const v = r[measureCol]; return typeof v === 'number' && v !== 0; }).length
      : report.data.length;
    return calculateChartHeight('popup', chartType, usableCount);
  }, [report, isBarType, isWaterfall, chartType]);

  // Legend only for bar/waterfall with mixed polarity
  const showPolarity = report ? (isBarType || isWaterfall) && hasMixedPolarity(report) : false;

  // v3.47 - Click tooltip state
  const [tooltip, setTooltip] = useState<TooltipData | null>(null);

  // Compute absolute total for % calculation
  const absTotal = useMemo(() => {
    if (!sortedReport?.yColumn) return 0;
    return sortedReport.data.reduce((sum, r) => {
      const v = Number(r[sortedReport.yColumn!]) || 0;
      return sum + Math.abs(v);
    }, 0);
  }, [sortedReport]);

  // v3.49 - Story 9E: Chart-type-adaptive click handler
  const handlePlotClick = useCallback((event: any) => {
    if (!event?.points?.length || !sortedReport) return;
    const pt = event.points[0];

    // Build tooltip data based on chart type
    let label = '';
    let value = 0;

    if (isBarType || isWaterfall) {
      // v3.54 - Story 9I: Match by label, not index. Plotly renders horizontal bars
      // bottom-to-top so pointIndex is reversed relative to sortedData order.
      if (!sortedReport.yColumn || !sortedReport.xColumn) return;
      const clickedLabel = pt.y; // Plotly horizontal bar: y = category label
      const truncate = (s: string, max = 30) => s.length > max ? s.substring(0, max - 1) + '\u2026' : s;
      const row = sortedReport.data.find(r => {
        const lbl = truncate(String(r[sortedReport.xColumn!] ?? ''), 30);
        return lbl === clickedLabel;
      });
      if (!row) return;
      label = String(row[sortedReport.xColumn] ?? '');
      value = Number(row[sortedReport.yColumn] ?? 0);
    } else if (isPieType) {
      label = pt.label ?? String(pt.y ?? '');
      value = Number(pt.value ?? pt.v ?? 0);
    } else if (isLineType) {
      label = String(pt.x ?? '');
      value = Number(pt.y ?? 0);
    } else {
      return; // No tooltip for unknown types
    }

    const pctOfTotal = absTotal > 0 ? (Math.abs(value) / absTotal) * 100 : 0;
    const sign: TooltipData['sign'] = value > 0 ? 'Positive' : value < 0 ? 'Negative' : 'Zero';

    const mouseEvent = event.event as MouseEvent | undefined;
    if (!mouseEvent) return;
    const anchor = document.createElement('div');
    anchor.style.position = 'fixed';
    anchor.style.left = `${mouseEvent.clientX}px`;
    anchor.style.top = `${mouseEvent.clientY}px`;
    anchor.style.width = '1px';
    anchor.style.height = '1px';
    anchor.style.pointerEvents = 'none';
    document.body.appendChild(anchor);

    setTooltip({ anchorEl: anchor, label, value, pctOfTotal, sign, chartCategory });
  }, [sortedReport, absTotal, isBarType, isPieType, isLineType, isWaterfall, chartCategory]);

  const closeTooltip = useCallback(() => {
    if (tooltip?.anchorEl) {
      tooltip.anchorEl.remove();
    }
    setTooltip(null);
  }, [tooltip]);

  // Download PNG via Plotly.downloadImage
  const handleDownload = () => {
    const plotEl = plotRef.current?.querySelector('.js-plotly-plot') as HTMLElement | null;
    if (plotEl && (window as any).Plotly) {
      (window as any).Plotly.downloadImage(plotEl, {
        format: 'png', width: 1200, height: chartHeight, filename: report?.title ?? 'chart',
      });
    }
  };

  // v3.45 - Story 9B: Reset sort to default when dialog closes
  const handleClose = () => {
    closeTooltip();
    setSortMode('abs');
    onClose();
  };

  return (
    <Dialog open={!!report} onClose={handleClose} fullWidth maxWidth="lg">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
        <Box>
          <Typography variant="h6" component="span">{title}</Typography>
          {subtitle && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>{subtitle}</Typography>
          )}
        </Box>
        <IconButton onClick={handleClose} size="small" aria-label="Close"><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }} ref={plotRef}>
        {/* v3.49 - Story 9E: Sort toggle — only for bar chart types */}
        {isBarType && report?.yColumn && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, pt: 1.5, pb: 0.5 }}>
            <SortIcon fontSize="small" color="action" />
            <Typography variant="caption" color="text.secondary">Sort:</Typography>
            <ToggleButtonGroup
              value={sortMode}
              exclusive
              size="small"
              onChange={(_, v) => { if (v) setSortMode(v); }}
            >
              <ToggleButton value="abs" sx={{ textTransform: 'none', px: 1.5, py: 0.25, fontSize: '0.75rem' }}>
                Largest first
              </ToggleButton>
              <ToggleButton value="positive" sx={{ textTransform: 'none', px: 1.5, py: 0.25, fontSize: '0.75rem' }}>
                Positive first
              </ToggleButton>
              <ToggleButton value="negative" sx={{ textTransform: 'none', px: 1.5, py: 0.25, fontSize: '0.75rem' }}>
                Negative first
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        )}
        {sortedReport && (
          <PlotlyChart report={sortedReport} viz={sortedReport.visualization} onPlotClick={handlePlotClick} skipSort />
        )}
      </DialogContent>

      {/* v3.47 - Story 9C: Bar click detail tooltip */}
      <Popover
        open={!!tooltip}
        anchorEl={tooltip?.anchorEl}
        onClose={closeTooltip}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{ paper: { sx: { maxWidth: 320, boxShadow: 6 } } }}
      >
        {tooltip && (
          <Card elevation={0}>
            <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                {tooltip.label}
              </Typography>
              <Divider sx={{ mb: 1 }} />
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {formatCurrency(tooltip.value)}
              </Typography>
              {/* v3.49 - Adaptive tooltip content per chart type */}
              {(tooltip.chartCategory === 'bar' || tooltip.chartCategory === 'waterfall') && (
                <>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {tooltip.pctOfTotal.toFixed(1)}% of total
                  </Typography>
                  <Chip
                    label={tooltip.sign}
                    size="small"
                    sx={{
                      mt: 0.75, height: 20, fontSize: '0.7rem',
                      bgcolor: tooltip.sign === 'Positive'
                        ? PALETTES.financialPolarity.light.positive
                        : tooltip.sign === 'Negative'
                          ? PALETTES.financialPolarity.light.negative
                          : undefined,
                      color: tooltip.sign !== 'Zero' ? '#fff' : undefined,
                    }}
                    variant={tooltip.sign === 'Zero' ? 'outlined' : 'filled'}
                  />
                </>
              )}
              {tooltip.chartCategory === 'pie' && (
                <Typography variant="caption" color="text.secondary" display="block">
                  {tooltip.pctOfTotal.toFixed(1)}% of whole
                </Typography>
              )}
              {tooltip.chartCategory === 'line' && (
                <Typography variant="caption" color="text.secondary" display="block">
                  at {tooltip.label}
                </Typography>
              )}
            </CardContent>
          </Card>
        )}
      </Popover>

      <DialogActions sx={{ justifyContent: 'space-between', px: 2 }}>
        {/* v3.43 - Color legend when financial polarity applies */}
        {showPolarity ? (
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="caption" color="text.secondary">Legend:</Typography>
            <Chip label="Positive" size="small" sx={{ bgcolor: PALETTES.financialPolarity.light.positive, color: '#fff', height: 22 }} />
            <Chip label="Negative" size="small" sx={{ bgcolor: PALETTES.financialPolarity.light.negative, color: '#fff', height: 22 }} />
            <Chip label="Zero" size="small" variant="outlined" sx={{ height: 22 }} />
          </Stack>
        ) : (
          <Box /> /* spacer */
        )}
        <Button startIcon={<DownloadIcon />} size="small" onClick={handleDownload}>Download PNG</Button>
      </DialogActions>
    </Dialog>
  );
}
